import { Features, selectHasFeature } from "@/store/features/features-slice";
import { selectModeName } from "@/store/mode-selectors";
import { selectFlyWalkSceneFilter } from "@/store/modes/flywalk-mode-slice";
import { selectWalkCompareSceneFilter } from "@/store/modes/walk-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectGuidedSplatsNavigation } from "@/store/view-options/view-options-selectors";
import { setGuidedSplatsNavigation } from "@/store/view-options/view-options-slice";
import { SceneFilter } from "@/types/scene-filter";
import { useLocalStorage } from "@faro-lotv/app-component-toolbox";
import { useCallback, useEffect, type JSX } from "react";
import { ViewOptionSection } from "./view-options-section";

type GuidedSplatsNavigationToggleProps = {
  /** Whether guided splats navigation is enabled */
  guidedSplatsNavigationOn: boolean;
  /** Callback issued when the guided splats navigation switch is toggled */
  onToggled(on: boolean): void;
};

/** @returns a switch control to enable/disable guided navigation through Gaussian Splats */
export function GuidedSplatsNavigationToggle({
  guidedSplatsNavigationOn,
  onToggled,
}: GuidedSplatsNavigationToggleProps): JSX.Element | null {
  const modeName = useAppSelector(selectModeName);
  const isAllowedInMode = modeName === "walk" || modeName === "split";

  const sceneFilter = useAppSelector(selectFlyWalkSceneFilter);
  const compareSceneFilter = useAppSelector(selectWalkCompareSceneFilter);

  const hasFeature = useAppSelector(
    selectHasFeature(Features.GuidedSplatsNavigation),
  );

  if (
    !hasFeature ||
    !isAllowedInMode ||
    (sceneFilter !== SceneFilter.GaussianSplats && modeName === "walk") ||
    (sceneFilter !== SceneFilter.GaussianSplats &&
      compareSceneFilter !== SceneFilter.GaussianSplats &&
      modeName === "split")
  ) {
    return null;
  }

  return (
    <ViewOptionSection
      label="Guided Gaussian Splats navigation"
      isChecked={guidedSplatsNavigationOn}
      onToggled={onToggled}
    />
  );
}

/**
 * Initializes the guided splats navigation enablement status from the browser's local storage,
 * and keeps the local storage state in sync with the Redux store.
 *
 * @returns the enablement status of the guided navigation and a callback to issue when the switch is toggled
 */
export function useGuidedSplatsNavigationToggleLogic(): GuidedSplatsNavigationToggleProps {
  const dispatch = useAppDispatch();
  const guidedSplatsNavigationEnabledInStore = useAppSelector(
    selectGuidedSplatsNavigation,
  );

  // 'guidedSplatsNavigationEnabledInStore' is only used to initialize the local storage state,
  // if no previous status exists in local storage.
  const [guidedSplatsNavigationOn, setGuidedSplatsNavigationOn] =
    useLocalStorage(
      "guidedSplatsNavigationOn",
      guidedSplatsNavigationEnabledInStore,
    );

  useEffect(() => {
    if (guidedSplatsNavigationEnabledInStore !== guidedSplatsNavigationOn) {
      dispatch(setGuidedSplatsNavigation(guidedSplatsNavigationOn));
    }
  }, [
    dispatch,
    guidedSplatsNavigationEnabledInStore,
    guidedSplatsNavigationOn,
  ]);

  const onToggled = useCallback(
    (on: boolean) => {
      setGuidedSplatsNavigationOn(on);
    },
    [setGuidedSplatsNavigationOn],
  );

  return {
    guidedSplatsNavigationOn,
    onToggled,
  };
}
